import {
	CheckListModule,
	Modules,
	ModuleStage,
	Stages,
	Steps,
	UpdateCheckListBarDataPayload,
	UpdateCheckListBarDataRequest,
	WelcomeVideoStatus
} from "./../contracts";
import { useContext } from "react";
import { ParentContext, ParentContextProps } from "./../store/context";
import {
	OnboardingActionCreators,
	onbordingStore,
	useOnboardingDispatch
} from "./../store";
import { CurrentGuideProgress } from "./../store/reducers";
import useOnboardingMaps from "./useOnboardingMaps";
import { getVendorDetails, getVendorResourcesByVendorId } from "../services";
import { ROOT_RESOURCE_ID } from "@/Contracts/Vendor/Resources";
import { VendorSubscriptionStatus } from "@/Contracts/Marketplace/Vendor";

export const getIncompleteStageDetail = (
	activeModuleIndex: number
): CurrentGuideProgress => {
	const { moduleOrder, checkListBarData, onboardingMaps } =
		onbordingStore.getState();
	const moduleConfig = checkListBarData?.modules?.[activeModuleIndex];
	let activeModule: Modules | null = null,
		activeStage: Stages | null = null,
		activeStep: Steps | null = null;
	if (moduleConfig?.stages && onboardingMaps) {
		const stageIndex = moduleConfig.stages.findIndex(
			completed => !completed.is_complete
		);
		activeModule = moduleOrder[activeModuleIndex];
		const currentStages = onboardingMaps.moduleStages[activeModule];
		activeStage = currentStages[stageIndex];
		activeStep = onboardingMaps.stageSteps[activeStage]?.[0]?.id ?? null;
	}
	return { activeModule, activeStage, activeStep };
};

export const getModuleDetails = (
	moduleName: Modules
): CheckListModule | null => {
	const { moduleOrder, checkListBarData } = onbordingStore.getState();
	const modulesIndex = moduleOrder.indexOf(moduleName);
	return checkListBarData?.modules?.[modulesIndex] ?? null;
};

/**
 * The method calculate the next guide states based on current guide state.
 * @return {CurrentGuideProgress} - returns next guide state
 */
export const getNextSteps = (): CurrentGuideProgress => {
	const { currentGuideProgress, checkListBarData, onboardingMaps } =
		onbordingStore.getState();
	let { activeModule, activeStage, activeStep } = currentGuideProgress;
	if (activeModule && activeStage && onboardingMaps) {
		// move to next step based on store data
		const currentSteps = onboardingMaps.stageSteps[activeStage];
		const stepIndex = currentSteps.findIndex(step => step.id == activeStep);
		if (stepIndex >= currentSteps.length - 1) {
			//move to next module or stage and steps
			const currentStages = onboardingMaps.moduleStages[activeModule];
			const stageIndex = currentStages.indexOf(activeStage);
			if (stageIndex >= currentStages.length - 1) {
				//move to next module
				activeModule = null;
				activeStage = null;
				activeStep = null;
			} else {
				//move to next stage and step
				activeStage = currentStages[stageIndex + 1];
				activeStep =
					onboardingMaps.stageSteps[activeStage][0]?.id ?? null;
			}
		} else {
			//move to next step
			activeStep = currentSteps[stepIndex + 1].id;
		}
	} else {
		// move to next step based on api data
		const activeModuleIndex = checkListBarData?.active_module ?? null;
		if (activeModuleIndex !== null) {
			return getIncompleteStageDetail(activeModuleIndex);
		} else {
			//move to next module
			activeModule = null;
			activeStage = null;
			activeStep = null;
		}
	}
	return { activeModule, activeStage, activeStep };
};

export const navigateToNextModule = (
	stepDetail: CurrentGuideProgress,
	parentContext: ParentContextProps | null
) => {
	if (stepDetail.activeModule) {
		const moduleDetail = getModuleDetails(stepDetail.activeModule);
		const entityId = moduleDetail?.created_entity_id ?? "";
		parentContext?.handles?.handleRedirect?.(stepDetail, entityId);
	}
};

/**
 * The hook returns a init method to trigger the onboarding guides
 * @return {{initOnboarding: Function}} - returns a method that makes api call to get guide progress data and init the guide ui
 */

export const useInitOnboarding = () => {
	const parentContext = useContext(ParentContext);
	const dispatch = useOnboardingDispatch();
	const initializeMaps = useOnboardingMaps();

	const checkVendorResources = async (modules: CheckListModule[]) => {
		const isAlignUser = modules?.[0]?.name === Modules.ALIGN;
		if (!isAlignUser) return true;

		try {
			const vendorId = parentContext?.config
				.signUpInitiatingVendor as string;
			const vendorCompany = await getVendorDetails(vendorId);
			// if partner is not part of vendor anymore but onboarding is not done yet
			if (vendorCompany.status !== VendorSubscriptionStatus.NOTAPPLIED)
				return true;
			// @TODO: Kartikay - replace with resources count api instead
			const resources = await getVendorResourcesByVendorId(
				vendorId,
				ROOT_RESOURCE_ID
			);

			const currentModules = [...modules];
			const alignModule = currentModules[0];
			const stages = alignModule.stages;

			if (!resources.data.length) {
				const newStages: ModuleStage[] = stages.map(stage => ({
					...stage,
					is_complete: true
				}));

				// Mark other 2 stages as complete.
				newStages[0].is_complete = false;
				alignModule.stages = newStages;
			} else {
				// Make 3 stages
				alignModule.stages =
					stages.length === 1
						? [
								stages[0],
								{
									name: Stages.ALIGN_VENDORS_AND_RESOURCES,
									is_complete: false
								},
								{
									name: Stages.ALIGN_EXPLORE_RESOURCES,
									is_complete: false
								}
						  ]
						: stages;
				currentModules[0] = alignModule;
			}

			const requestPayload: UpdateCheckListBarDataPayload = {
				modules: currentModules
			};

			const request: UpdateCheckListBarDataRequest = {
				requestPayload,
				hasAPIUpdate: true
			};

			await dispatch(
				OnboardingActionCreators.updateCheckListBarData(request)
			);

			return !!!resources.data.length;
		} catch (error) {
			console.error(error);
		}

		return true;
	};

	const initOnboarding = async (
		isAuthenticatedFromSignIn = false,
		isPaymentNeeded = false
	) => {
		const hasBotSource =
			parentContext?.config?.userData?.bot_user_source !== null;
		if (isPaymentNeeded || hasBotSource) return;

		try {
			const { welcome_video, is_checklist_active, modules } =
				await dispatch(OnboardingActionCreators.getChecklistBarData());

			//set module order after begin
			const moduleOrder = modules.map(module => {
				return module.name as Modules;
			});
			if (moduleOrder) {
				dispatch(
					OnboardingActionCreators.updateModuleOrder(moduleOrder)
				);
			}

			// fetch vendor resources for align flow beforehand to update stages accordingly.
			const isResourcesEmpty = await checkVendorResources(modules);

			// set onboarding maps
			const onboardingMaps = initializeMaps(isResourcesEmpty);
			dispatch(
				OnboardingActionCreators.updateOnboardingMaps(onboardingMaps)
			);

			const showWelcomeVideo =
				isAuthenticatedFromSignIn &&
				welcome_video?.status &&
				![
					WelcomeVideoStatus.WATCHED,
					WelcomeVideoStatus.DONT_SHOW_AGAIN
				].includes(welcome_video?.status);

			if (showWelcomeVideo) {
				dispatch(
					OnboardingActionCreators.updateActiveProgressData({
						activeModule: null,
						activeStage: null,
						activeStep: Steps.WELCOME_VIDEO
					})
				);
				dispatch(
					OnboardingActionCreators.updateProgressInitilization(true)
				);
			}

			//return if onboarding is disabled
			if (!is_checklist_active) {
				return;
			}

			//just getting next steps
			const newSteps = getNextSteps();

			// check for welcome videos status
			if (!showWelcomeVideo) {
				dispatch(
					OnboardingActionCreators.updateActiveProgressData(newSteps)
				);
				dispatch(
					OnboardingActionCreators.updateProgressInitilization(true)
				);
			}

			if (newSteps?.activeModule) {
				navigateToNextModule(newSteps, parentContext);
			}

			await dispatch(OnboardingActionCreators.getDummyClientData());
			await dispatch(OnboardingActionCreators.getDocumentTemplateData());
		} catch (e) {
			console.error(e);
		}
	};
	return { initOnboarding };
};
